import { gql } from '@apollo/client';
import {
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import { Permission, Role } from '@tp-vision/roles-permissions';
import { format } from 'date-fns';
import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useAuth } from '~auth/useAuth';
import { useAuthentication } from '~auth/useAuthentication';
import { TruncatedCell } from '~components/organization/AccessRequests/AccessRequestsSection';
import { useDestructiveAction } from '~components/ui/DestructiveAction';
import { ArrowRightIcon, MoreIconVertical } from '~components/ui/icons';
import { ServiceAdminGlobalSearchAccessState } from '~graphql/__generated__/types';
import { HandleApiError } from '~graphql/HandleApiError';
import { getRoleOptionsForRoles } from '~pages/[organization]/settings/users/constants';
import { goToOrganization } from '~utils/elevatedOrganization';
import { fromError } from '~utils/errors';
import {
  ServiceAdminSearchUsersQuery,
  useServiceAdminResendInviteMutation,
  useServiceAdminSearchUsersQuery,
  useServiceAdminUserDeleteMutation,
} from './__generated__/UserSearchResults.graphql';
import { EditUserModal } from './EditUserModal';
import { SearchResultColumn, SearchResultsTable, useSearchPagination } from './SearchResultsTable';
import { getUserManagementErrorMap } from './userManagement';

type UserResult = ServiceAdminSearchUsersQuery['serviceAdminSearchUsers']['results'][number];

// Roles that only exist for bookkeeping and say nothing about what the user can do.
const HIDDEN_ROLES = [Role.Mfa, Role.SsoUser];

function getRoleLabels(roles: Role[]) {
  return getRoleOptionsForRoles(roles.filter((role) => !HIDDEN_ROLES.includes(role))).map(
    ({ label }) => label,
  );
}

export function formatRoles(roles: Role[]) {
  const labels = getRoleLabels(roles);

  return labels.length > 1 ? `${labels[0]}, +${labels.length - 1}` : labels[0] ?? '-';
}

function RolesCell({ roles }: { roles: Role[] }) {
  return (
    <Tooltip label={getRoleLabels(roles).join(', ')} placement="top" openDelay={300}>
      <span>{formatRoles(roles)}</span>
    </Tooltip>
  );
}

function fullName(user: UserResult) {
  return [user.givenName, user.familyName].join(' ').trim();
}

export function UserSearchResults({ searchTerm }: { searchTerm: string }) {
  const { t } = useTranslation();
  const pagination = useSearchPagination();
  const [editingUser, setEditingUser] = useState<UserResult | null>(null);

  const { data, loading, error, refetch } = useServiceAdminSearchUsersQuery({
    variables: { searchTerm, pageSize: pagination.pageSize, pageNumber: pagination.pageIndex },
    fetchPolicy: 'cache-and-network',
  });

  const columns = useMemo<Array<SearchResultColumn<UserResult>>>(
    () => [
      {
        id: 'firstName',
        header: t('firstName'),
        render: (user) => (
          <TruncatedCell text={user.givenName ?? '-'} maxWidth="40" fontWeight="semibold" />
        ),
      },
      {
        id: 'lastName',
        header: t('lastName'),
        render: (user) => <TruncatedCell text={user.familyName ?? '-'} maxWidth="40" />,
      },
      {
        id: 'email',
        header: t('email'),
        render: (user) => <TruncatedCell text={user.email} maxWidth="64" />,
      },
      { id: 'role', header: t('role'), render: (user) => <RolesCell roles={user.roles} /> },
      {
        id: 'organization',
        header: t('organisation'),
        render: (user) => <TruncatedCell text={user.organizationName} maxWidth="48" />,
      },
      {
        id: 'lastLogin',
        header: t('lastLogin'),
        render: (user) =>
          user.lastLogin
            ? format(new Date(user.lastLogin), 'HH:mm:ss ; dd/MMM/yyyy')
            : t('pending'),
      },
      {
        id: 'actions',
        header: '',
        width: '16',
        render: (user) => (
          <UserActions user={user} onEdit={() => setEditingUser(user)} onChanged={refetch} />
        ),
      },
    ],
    [t, refetch],
  );

  if (error) {
    return <HandleApiError error={error} />;
  }

  return (
    <>
      <SearchResultsTable
        isLoading={loading && !data}
        rows={data?.serviceAdminSearchUsers.results ?? []}
        totalCount={data?.serviceAdminSearchUsers.totalCount ?? 0}
        columns={columns}
        pagination={pagination}
      />
      <EditUserModal user={editingUser} onClose={() => setEditingUser(null)} onUpdated={refetch} />
    </>
  );
}

function UserActions({
  user,
  onEdit,
  onChanged,
}: {
  user: UserResult;
  onEdit: () => void;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const { verifyUserPermissions } = useAuth();
  const {
    user: { organizationId: homeOrganizationId },
  } = useAuthentication();
  const [resendInvite] = useServiceAdminResendInviteMutation();
  const [deleteUser] = useServiceAdminUserDeleteMutation();

  const canManageUsers = verifyUserPermissions([Permission.ServiceAdminUserManage]);
  // Users of the caller's own organization need no access, so there is no organization to go to.
  const hasAccess =
    user.organizationId !== homeOrganizationId &&
    user.accessState === ServiceAdminGlobalSearchAccessState.Available;

  const removeAction = useDestructiveAction<UserResult>({
    title: t('removeUser'),
    message: (target) => (
      <Trans
        i18nKey="adminSearchRemoveUserConfirm"
        values={{ name: fullName(target), organizationName: target.organizationName }}
        components={{ strong: <strong /> }}
      />
    ),
    notice: (target) =>
      target.roles.includes(Role.Owner) ? t('adminSearchRemoveOwnerNotice') : null,
    confirmLabel: t('adminSearchRemoveUserConfirmButton'),
    onConfirm: async (target) => {
      try {
        await deleteUser({ variables: { userId: target.id } });

        toast({
          status: 'success',
          title: t('adminSearchUserRemoved'),
          description: (
            <Trans
              i18nKey="adminSearchUserRemovedDescription"
              values={{
                name: fullName(target),
                organizationName: target.organizationName,
                role: getRoleLabels(target.roles).join(', '),
              }}
              components={{ strong: <strong /> }}
            />
          ),
        });

        onChanged();
      } catch (err) {
        toast({
          status: 'error',
          title: t('adminSearchUserRemoveError'),
          description: fromError(err, 'ServiceAdminUserDelete', getUserManagementErrorMap()),
        });
      }
    },
  });

  const handleResendInvite = async () => {
    try {
      await resendInvite({ variables: { userId: user.id } });

      toast({
        status: 'success',
        title: (
          <Trans
            i18nKey="adminSearchInviteResent"
            values={{ email: user.email }}
            components={{ strong: <strong /> }}
          />
        ),
      });
    } catch (err) {
      toast({
        status: 'error',
        title: t('adminSearchInviteResendError'),
        description: fromError(err, 'ServiceAdminResendInvite', getUserManagementErrorMap()),
      });
    }
  };

  if (!canManageUsers && !hasAccess) {
    return null;
  }

  return (
    <>
      <Menu placement="bottom-end">
        <MenuButton
          as={IconButton}
          variant="unstyled"
          display="flex"
          alignItems="center"
          justifyContent="center"
          minWidth="8"
          height="8"
          aria-label={t('actions')}
          icon={<MoreIconVertical color="blue.400" width="5" height="5" />}
        />
        <MenuList>
          {canManageUsers && !user.lastLogin && (
            <MenuItem onClick={handleResendInvite}>{t('resendInvite')}</MenuItem>
          )}
          {canManageUsers && <MenuItem onClick={onEdit}>{t('edit')}</MenuItem>}
          {canManageUsers && (
            <MenuItem onClick={() => removeAction.askConfirmation(user)}>
              {t('removeUser')}
            </MenuItem>
          )}
          {hasAccess && (
            <MenuItem
              onClick={() => goToOrganization(user.organizationId)}
              justifyContent="space-between"
            >
              {t('accessAvailableGoToOrgLower')}
              <ArrowRightIcon marginLeft="4" />
            </MenuItem>
          )}
        </MenuList>
      </Menu>
      {removeAction.confirmationNode}
    </>
  );
}

UserSearchResults.graphql = {
  queries: {
    ServiceAdminSearchUsers: gql`
      query ServiceAdminSearchUsers($searchTerm: String!, $pageSize: Int, $pageNumber: Int) {
        serviceAdminSearchUsers(
          searchTerm: $searchTerm
          pageSize: $pageSize
          pageNumber: $pageNumber
        ) {
          totalCount
          results {
            id
            email
            givenName
            familyName
            organizationId
            organizationName
            roles
            lastLogin
            accessState
          }
        }
      }
    `,
  },
  mutations: {
    ServiceAdminResendInvite: gql`
      mutation ServiceAdminResendInvite($userId: ID!) {
        serviceAdminUserResendInvite(userId: $userId)
      }
    `,
    ServiceAdminUserDelete: gql`
      mutation ServiceAdminUserDelete($userId: ID!) {
        serviceAdminUserDelete(userId: $userId) {
          userId
        }
      }
    `,
  },
};
